import * as React from 'react'
import {ICellRendererParams} from 'ag-grid-community'
import {observer} from 'mobx-react-lite'
import {Typography} from '@mui/material'
import {makeOcStyles} from '@oc/styles'
import {toString} from '@oc/utils'

const useStyles = makeOcStyles(theme => ({
    root: {
        padding: theme.spacing(1)
    },
    headerRoot: {
        color: theme.palette.text.primary
    },
    wrapText: {
        whiteSpace: 'break-spaces',
        overflowWrap: 'anywhere'
    },
    textHeight: {
        minHeight: 22
    },
    ellipsesText: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minHeight: 22
    },
    editableCell: {
        color: theme.palette.action.active
    },
    tooltipRoot: {
        padding: theme.spacing(1)
    },
    tooltipLanguageName: {
        lineHeight: 'normal'
    },
    tooltipLanguageTranslation: {
        lineHeight: 'normal',
        whiteSpace: 'break-spaces',
        overflowWrap: 'anywhere'
    },
    bold: {
        fontWeight: 700
    }
}))

export interface TwoLineColumnRendererProps<T> extends ICellRendererParams {
    /** When getValueFirstLine = undefined, value = data[fieldFirstLine] */
    fieldFirstLine?: keyof T
    /** When specified used instead of default value = data[fieldFirstLine] */
    getValueFirstLine?: (data: T) => any
    /** When getValueSecondLine = undefined, value = data[fieldSecondLine] */
    fieldSecondLine?: keyof T
    /** When specified used instead of default value = data[fieldSecondLine] */
    getValueSecondLine?: (data: T) => any
    editable?: boolean
    switchLinesBold?: boolean
    data: T | undefined
    showBasicTooltip?: boolean
    CustomTooltipComponent?: ({data}: {data: T}) => JSX.Element
}

function getValue<T>(data?: T, getValue?: (data: T) => any, fieldName?: keyof T): string {
    if (data && getValue) {
        return toString(getValue(data))
    }

    if (data && fieldName) {
        return toString(data[fieldName])
    }

    return ''
}

function _TwoLineColumnRenderer<T>(props: TwoLineColumnRendererProps<T>) {
    const {classes, cx} = useStyles()
    const {data, editable, fieldFirstLine, getValueFirstLine, fieldSecondLine, getValueSecondLine, switchLinesBold} =
        props
    const top = getValue<T>(data, getValueFirstLine, fieldFirstLine)
    const bottom = getValue<T>(data, getValueSecondLine, fieldSecondLine)

    return (
        <div
            className={cx(classes.root, {
                [classes.headerRoot]: !data,
                [classes.editableCell]: editable && !!data
            })}
        >
            <Typography
                className={cx(classes.textHeight, classes.ellipsesText, {
                    [classes.bold]: !switchLinesBold
                })}
            >
                {top}
            </Typography>
            {bottom && bottom !== '' && (
                <Typography
                    className={cx(classes.textHeight, classes.ellipsesText, {
                        [classes.bold]: switchLinesBold
                    })}
                >
                    {bottom}
                </Typography>
            )}
        </div>
    )
}

export const TwoLineColumnRenderer = observer(_TwoLineColumnRenderer)
